import React, { FC, memo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { WidgetLayout } from '../../../lib/widget-layout'
import useText from '../../../common/hooks/useText'
import DatePicker from '../../../common/components/DatePicker'
import { Button } from '../../../common/components/Button'
import {
    useCompanyDeleteChargingSessionsMutation,
    useCompanyGetChargingSessionsByRangeQuery,
} from '../../../common/redux/api/web-api'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../../lib/table'
import { dateLocalize } from '../../../lib/date-localize'
import { notifyError, notifySuccess } from '../../../common/features/notificationsManager/Notify'
import ConfirmationModal from '../../../ui/ConfirmationModal'

type ChargingSessionWidgetProps = { companyId: string }

type FormData = {
    startTime: string
    endTime: string
}

export const ChargingSessionWidget: FC<ChargingSessionWidgetProps> = memo(({ companyId }) => {
    const text = useText()
    const [confirmationModalIsOpened, setConfirmationModalIsOpened] = useState(false)
    const [dateRange, setDateRange] = useState<FormData>({ startTime: '', endTime: '' })
    const { currentData: chargingSessions = [], isFetching } = useCompanyGetChargingSessionsByRangeQuery(
        {
            companyId,
            ...dateRange,
        },
        { skip: !dateRange.startTime && !dateRange.endTime }
    )
    const [deleteChargingSessions] = useCompanyDeleteChargingSessionsMutation()

    const {
        register,
        handleSubmit,
        formState: { isDirty },
    } = useForm<FormData>({
        mode: 'all',
    })

    const handleChargingSession = (data: FormData) => {
        setDateRange(data)
    }

    const handleRemoveChargingSessions = () => {
        setConfirmationModalIsOpened(false)
        deleteChargingSessions({ ...dateRange, companyId })
            .unwrap()
            .then(() => notifySuccess(text('msgDeleteSessionsSuccess')))
            .catch(() => notifyError(text('msgCouldNotDeleteSessions')))
    }

    const renderChargingSessions = () => {
        if (chargingSessions && chargingSessions.length === 0) {
            return
        }
        return (
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell>{text('lblStart')}</TableHeaderCell>
                        <TableHeaderCell>{text('lblEnd')}</TableHeaderCell>
                        <TableHeaderCell>{text('lblCharger')}</TableHeaderCell>
                        <TableHeaderCell>{text('lblConsumption')}</TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {chargingSessions?.map((session) => (
                        <TableRow key={session.id}>
                            <TableBodyCell dataTestId={`session-start-${session.id}`}>
                                {dateLocalize(session.startTime)}
                            </TableBodyCell>
                            <TableBodyCell dataTestId={`session-end-${session.id}`}>
                                {dateLocalize(session.endTime)}
                            </TableBodyCell>
                            <TableBodyCell dataTestId={`session-chargepoint-id-${session.id}`}>
                                {session.chargePointId}
                            </TableBodyCell>
                            <TableBodyCell
                                dataTestId={`session-total-consuption-${session.id}`}
                            >{`${session.totalConsumptionKwh ?? '---'} ${text('lblKWh')}`}</TableBodyCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        )
    }

    return (
        <WidgetLayout title={text('lblChargingSessionsWidget')}>
            <form onSubmit={handleSubmit(handleChargingSession)}>
                <DatePicker
                    wrapperClassName="mt-5"
                    {...register('startTime')}
                    data-testid="from-input"
                    id="from-date"
                    labelText={text('lblFromDate')}
                />
                <DatePicker
                    wrapperClassName="mt-5"
                    {...register('endTime')}
                    data-testid="to-input"
                    id="to-date"
                    labelText={text('lblToDate')}
                />
                <div className="mt-5 flex justify-content-end">
                    <Button
                        loading={isFetching}
                        size="sm"
                        type="submit"
                        label={text('btnGet')}
                        disabled={!isDirty}
                        data-testid="button-get"
                    />
                    <Button
                        className="ml-3"
                        size="sm"
                        label={text('btnClear')}
                        disabled={chargingSessions.length === 0}
                        onClick={() => setConfirmationModalIsOpened(true)}
                        data-testid="button-clear"
                    />
                </div>
                <div className="mt-5 overflow-y-auto max-h-30rem">{renderChargingSessions()}</div>
            </form>
            {confirmationModalIsOpened && (
                <ConfirmationModal
                    title={text('lblChargingSessionsWidget')}
                    message={text('lblConfirmDeletingChargingSession')}
                    onConfirm={() => handleRemoveChargingSessions()}
                    onClose={() => setConfirmationModalIsOpened(false)}
                />
            )}
        </WidgetLayout>
    )
})
